import { createClient, SupabaseClient, SupabaseClientOptions } from '@supabase/supabase-js'
import { Database } from './types.gen'

export type WillieSupabaseClient = SupabaseClient<Database>

export type TableNames = keyof Database['public']['Tables']

export type SupabaseConfig = {
  supabaseUrl?: string
  projectId?: string
  /**
   * This is either the anon key for the client or the service key (secret)
   */
  key?: string
  options?: SupabaseClientOptions<'public'>
}

export const makeSupabaseClient = ({
  supabaseUrl = process.env.SUPABASE_URL ?? '',
  projectId = process.env.SUPABASE_PROJECT_ID ?? '',
  key = process.env.SUPABASE_ANON_KEY ?? '',
  options
}: SupabaseConfig) => {
  const url = supabaseUrl || `https://${projectId}.supabase.co`

  return createClient<Database>(url, key, options)
}

export const makeSupabaseClientFromUserToken = (token: string) =>
  makeSupabaseClient({
    options: {
      global: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    }
  })

export const makeSuperAdminSupabaseClient = () =>
  makeSupabaseClient({ key: process.env.SUPABASE_SERVICE_ROLE_KEY ?? '' })

export const makeOrgScopedServiceAccountSupabaseClient = (orgID: string) => {
  // layzy load jwt to avoid browser bundling issues, if this causes issues we can refactor this in a seperate file to simplify tree shaking
  const jwt = require('jsonwebtoken')

  // NOTE:
  // do not change the sub it's used in RLS POLICIES
  // we could also create a custom role for this, but it would require more maintenance to grant access to all current and future tables
  const jwtPayload = {
    role: 'authenticated',
    exp: Math.floor(Date.now() / 1000) + 3600,
    sub: 'service_account', //DO NOT CHANGE
    org_id: orgID
  }

  const token = jwt.sign(jwtPayload, process.env.SUPABASE_JWT_SECRET ?? '', {
    algorithm: 'HS256'
  })

  return makeSupabaseClient({
    options: {
      global: {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    }
  })
}

export * from './types.gen'
